import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { Check, Clock3, Loader2, Sparkles } from 'lucide-react'
import { useEvaluateStartupMutation } from '../../../hooks/api'

const DELIVERABLES = [
  'Where this startup fits your thesis (and where it doesn’t)',
  'Persona-weighted strengths and red flags from your decision lens',
  'The questions your persona would hit them with first',
  'Risk register scored against your kill criteria and ticket size',
  'Verdict: invest / conditional / pass, with the reasoning your persona would give',
]

interface CustomDiligenceDialogProps {
  investorId: string
  startupId: string
  startupName?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after the mutation succeeds (e.g. to redirect or close). */
  onSuccess?: () => void
  /** Disable the action when the investor has no website — persona research can't run. */
  disabled?: boolean
}

function extractErrorMessage(error: unknown): string {
  if (!error) return 'Something went wrong'
  const body = (error as { body?: { message?: string } }).body
  if (body?.message) return body.message
  if (error instanceof Error) return error.message
  return 'Something went wrong'
}

export function CustomDiligenceDialog({
  investorId,
  startupId,
  startupName,
  open,
  onOpenChange,
  onSuccess,
  disabled = false,
}: CustomDiligenceDialogProps) {
  const evaluateMutation = useEvaluateStartupMutation()

  const isPending = evaluateMutation.isPending
  const isSuccess = evaluateMutation.isSuccess
  const isError = evaluateMutation.isError

  const handleStart = (e: React.MouseEvent) => {
    e.preventDefault()
    if (isPending || isSuccess) return
    evaluateMutation.mutate({ path: { investorId, startupId } })
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return // don't allow close mid-flight
    if (!next) {
      const wasSuccess = isSuccess
      evaluateMutation.reset()
      onOpenChange(next)
      if (wasSuccess) onSuccess?.()
      return
    }
    onOpenChange(next)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
              <Sparkles className="size-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                AI diligence · your persona
              </p>
              <AlertDialogTitle className="truncate text-xl leading-tight">
                {startupName ?? 'Run custom diligence'}
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
            We rebuild your decision lens from your thesis, kill criteria and ticket, then run this
            startup's deck, financials and team through it. You get a diligence brief written the
            way <em>your</em> persona would write it, not a generic report.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            What you'll get back
          </p>
          <ul className="space-y-2.5">
            {DELIVERABLES.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-xs">
          <Clock3 className="size-4 text-foreground/70" />
          <div className="leading-tight">
            <div className="font-semibold text-foreground">5–10 minutes</div>
            <div className="text-[10px] text-muted-foreground">
              Leave the page and we'll save it
            </div>
          </div>
        </div>

        {isSuccess && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <p className="text-sm font-medium text-emerald-300">Diligence started</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              We'll notify you when the brief is ready in your feed.
            </p>
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
            <p className="text-sm font-medium text-red-300">
              {extractErrorMessage(evaluateMutation.error)}
            </p>
          </div>
        )}

        {disabled && (
          <div className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Custom diligence requires a website on your investor profile.
            </p>
          </div>
        )}

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel className="sm:mr-auto" disabled={isPending}>
            {isSuccess ? 'Close' : 'Not now'}
          </AlertDialogCancel>
          {!isSuccess && (
            <AlertDialogAction onClick={handleStart} disabled={isPending || disabled}>
              {isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Starting...
                </>
              ) : isError ? (
                'Try again'
              ) : (
                'Run diligence'
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
